/**
 * Runtime pack registry. The "active pack" is the one currently driving rounds.
 * Only the bundled packs ship — there is no custom-pack upload.
 */
import i18next from 'i18next';
import type { Pack } from '@/data/packs/types';
import { BUILT_IN_PACKS, packKey } from '@/data/packs';
import { journeyPoolsForLang, getHalloweenPool, HALLOWEEN_POOL_ID } from '@/data/pools';
import { getSelectedLessonKey, setSelectedLessonKey } from './storage';
import { urlParam } from './dev';
import { platform } from '@/platform';
import { eventActive } from './season';

/**
 * Active pack for the current session, mirrored to localStorage (see storage.ts) so the
 * player's last pick is restored on app open. The stored pick is kept PER LANGUAGE, so
 * switching de → en → de returns to the German lesson the player was on. If that language
 * has no stored pick, getActivePack falls back to its default lesson.
 */
let sessionActivePack: Pack | null = null;

export function allPacks(): Pack[] {
  return [...BUILT_IN_PACKS];
}

/**
 * Lessons available for a language, in pick order: the journey pools first (Daily Life, Adventure,
 * Fables, reading, Picture Words — see journeyPoolsForLang; the default lesson is Daily Life),
 * then that language's arcade packs. A lesson is always language-scoped — `de-CH` only ever lists
 * `de-CH` lessons (no fallback to `de` or other languages).
 */
export function lessonsForLang(lang: string): Pack[] {
  const lessons: Pack[] = [...journeyPoolsForLang(lang)];
  for (const p of BUILT_IN_PACKS) {
    if (p.lang === lang) lessons.push(p);
  }
  // A seasonal lesson is on offer only while its event runs, and goes at the FRONT of the picker so
  // it is the first card a player sees — but never first in the list the default is taken from
  // (see defaultLessonForCurrentLang), so the event can never become anybody's starting lesson.
  const halloween = eventActive('halloween', lang) ? getHalloweenPool(lang) : null;
  if (halloween) lessons.unshift(halloween);
  return lessons;
}

export function getActivePack(): Pack {
  if (sessionActivePack && sessionActivePack.lang === i18next.language) return sessionActivePack;
  // An ad's landing lesson outranks the stored pick, once (see urlLesson).
  const landing = urlLesson();
  if (landing) {
    setActivePack(landing);
    return landing;
  }
  // No valid session pick yet: restore the player's last stored pick if it's a lesson in the
  // current language, else fall back to the language default.
  sessionActivePack = restoreStoredLesson() ?? defaultLessonForCurrentLang();
  return sessionActivePack;
}

export function setActivePack(pack: Pack): void {
  sessionActivePack = pack;
  setSelectedLessonKey(pack.lang, packKey(pack));
}

/**
 * Drop the in-memory pick so the next getActivePack() re-resolves for the current UI language.
 * Called on a language switch. The STORED per-language picks are deliberately kept — that is what
 * lets a player return to a language and find the lesson they left it on.
 */
export function releaseActivePack(): void {
  sessionActivePack = null;
}

/**
 * A landing lesson named by the URL, consumed ONCE per page load: `?lesson=<packId>` opens that
 * lesson in whatever the current UI language is, so an ad can land on the lesson it advertises —
 * `?demo&lng=ko&lesson=fables-250` opens the Korean folk-tale lesson (도깨비, 구미호), which is the
 * thing the ad promised. Without it every campaign lands on Daily Life and has to hope the parent
 * goes looking.
 *
 * The second URL flag to reach production, after `?demo`, and scoped to stay boring:
 *
 * - **Browser only**, the same gate and the same reason as `isWebDemo()` in src/game/clip.ts: this
 *   writes the player's remembered lesson, so it must not be possible for a link to reach into an
 *   app somebody BOUGHT and move their child off the lesson they were on. On Android a link to the
 *   verified domain can open the TWA, so "no paid app" is a platform check, never a URL one. Inert
 *   in the TWA, the iOS app and the App Clip by construction.
 * - **The value is a packId, never a packKey.** The language comes from `lng` (or detection), so a
 *   single `?lesson=fables-250` works for every locale a campaign runs in and the ad copy never has
 *   to know about the `__<lang>` suffix.
 * - **An unknown id is ignored**, so a stale or mistyped ad URL degrades to the default lesson
 *   rather than erroring — an ad is the one link we cannot fix after it ships.
 * - **Consumed once.** Picking another lesson on the menu, or switching language (which calls
 *   releaseActivePack), must not be undone by the next getActivePack().
 * - **Persisted like a manual pick** (via setActivePack), because it *is* the visitor's starting
 *   lesson and it should still be there when they come back.
 */
let landingLessonConsumed = false;

function urlLesson(): Pack | null {
  if (landingLessonConsumed) return null;
  landingLessonConsumed = true;
  if (platform().platform !== 'web') return null;
  const id = urlParam('lesson');
  if (!id) return null;
  return lessonsForLang(i18next.language).find((p) => p.id === id) ?? null;
}

/** The lesson stored for the current UI language, if it is still a lesson that language offers. */
function restoreStoredLesson(): Pack | null {
  const key = getSelectedLessonKey(i18next.language);
  if (!key) return null;
  return lessonsForLang(i18next.language).find((p) => packKey(p) === key) ?? null;
}

/**
 * Lesson Survival should fly. Survival is a pure class-match mode: one rule banner ("Smash all
 * nouns!") and words raining down. A 'banner-context' lesson has no such rule — which tile is
 * correct depends on the bannered context word, so the gaps lesson ("Schieße das Stück ab, das
 * passt!") would rain context-free fragments (`ter`, `fel`, `ne`) with nothing to match them
 * against. A 'sentence-order' lesson fails harder still: it has no falling-word mode at all, since
 * correctness there depends on a slot strip Survival never draws, and the words would rain past the
 * only place they mean anything. Those lessons hand Survival the language's first class-match
 * lesson instead.
 */
function needsClassMatchForSurvival(p: Pack): boolean {
  return p.mode === 'banner-context' || p.mode === 'sentence-order';
}

export function survivalPack(): Pack {
  const active = getActivePack();
  if (!needsClassMatchForSurvival(active)) return active;
  const fallback = lessonsForLang(i18next.language)
    .find((p) => p.id !== HALLOWEEN_POOL_ID && !needsClassMatchForSurvival(p));
  return fallback ?? BUILT_IN_PACKS.find((p) => !needsClassMatchForSurvival(p)) ?? active;
}

/** Default lesson for the UI language: its Daily Life pool, else its first arcade pack, else any pack. */
function defaultLessonForCurrentLang(): Pack {
  const lessons = lessonsForLang(i18next.language).filter((p) => p.id !== HALLOWEEN_POOL_ID);
  if (lessons.length > 0) return lessons[0];
  // Last resort (language with neither a pool nor an arcade pack): any built-in pack.
  return BUILT_IN_PACKS[0];
}

export { packKey };
